import { ThingActivity, ThingEventSnapshot } from '@eagle/core-data-types';
import { EventRecordEventTypes, FeatureTypes, TrackingEventTypes } from '@eagle/data-function-types';
import { Theme } from '@mui/material';
import { DateTime } from 'luxon';
import { EventDefinition, SimpleActivityFeatures } from '../../notable-events';
import { Undefinable } from '../../types';
import { EntityItems } from './entity-journey-types';

export const getColorIndex = (items: EntityItems[]): number => {
  if (!items) return 0;

  const highestIndex = Math.max(...items.map((item) =>
    Math.max(...item.journeyList.map((journey) => journey.colorIndex)),
  ));

  return highestIndex < 0 ? 0 : highestIndex + 1;
};

export const getJourneyColor = (index: number, theme: Theme): string => {
  const numberOfColors = theme.colorMap.length;
  const i = index < 0 ? 0 : index % numberOfColors;

  return theme.colorMap[i];
};

export const getEventDictionary = ({ feature, featureTypeId, eventTypeId }: ThingEventSnapshot, theme: Theme, notableEvents: EventDefinition[]): Undefinable<EventDefinition> => {
  if (!notableEvents) return;

  if (featureTypeId === FeatureTypes.EVENT_RECORD_V0 && eventTypeId === EventRecordEventTypes.OCCURRENCE) {
    return notableEvents.find(({ featureId, eventTypeId: eventId }) => feature.startsWith(featureId) && eventTypeId === eventId);
  }

  const event = notableEvents.find(({ featureId, eventTypeId: eventId }) => featureTypeId === featureId && eventTypeId === eventId);

  if (event && event.styling && featureTypeId === FeatureTypes.SIMPLE_ACTIVITY_V0 && feature === SimpleActivityFeatures.SECURITY_ALARM_ALERT) {
    event.styling = { ...event?.styling, backgroundColor: theme.alerts.orange };
  }

  return event;
};

export const isValidEvent = (eventData: Undefinable<EventDefinition>, showLocationUpdates?: boolean): boolean => {
  if (!eventData) return false;
  const { enabled, notableEvent, eventTypeId } = eventData;
  const isLocationUpdateType = [
    TrackingEventTypes.LOCATION_UPDATE,
    TrackingEventTypes.INACCURATE_LOCATION_UPDATE,
    TrackingEventTypes.NO_LOCATION_UPDATE,
  ].includes(eventTypeId as TrackingEventTypes);
  return isLocationUpdateType ? !!showLocationUpdates : enabled && notableEvent;
};

export const exportCsv = (data: (string | number)[][], fileName: string): void => {
  // Convert the data array into a CSV string
  const csvString = data
    .map((row) => row.map((value) => {
      const stringValue = String(value);
      // Escape fields containing commas, double quotes, or newlines
      return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
        ? `"${stringValue.replace(/"/g, '""')}"`
        : stringValue;
    }).join(','))
    .join('\n');

  const utf8Bom = new Uint8Array([0xef, 0xbb, 0xbf]);
  const blob = new Blob([utf8Bom, csvString], { type: 'text/csv;charset=utf-8;' });

  // Generate a download link and initiate the download
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const getActivityNotableEvents = (events: ThingEventSnapshot[], activity: ThingActivity): ThingEventSnapshot[] => {
  return events.filter((eventData) => {
    return (!activity.finish || DateTime.fromJSDate(eventData.occurred) <= DateTime.fromJSDate(activity.finish))
      && DateTime.fromJSDate(eventData.occurred) >= DateTime.fromJSDate(activity.start)
      && eventData.eventTypeId !== TrackingEventTypes.LOCATION_UPDATE
      && eventData.eventTypeId !== TrackingEventTypes.INACCURATE_LOCATION_UPDATE
      && eventData.eventTypeId !== TrackingEventTypes.NO_LOCATION_UPDATE;
  });
};
